/**
 * `useViewerAccess` — which limited audiences the signed-in employee is in.
 *
 * The React half of `src/lib/viewer-access.ts`. The rail reads it to leave out
 * a page whose audience the viewer lacks. That is a display choice: the
 * handlers behind such a page refuse everybody else on their own.
 *
 * Loading and failure both read as no access, so a limited entry never
 * flashes into the rail for an employee outside its audience.
 *
 * The key names the viewer. The query client outlives a sign-out, and a second
 * employee signing in on the same tab must not inherit the first one's answer.
 */

import { useQuery } from '@tanstack/react-query'

import { fetchViewerAccess } from '../lib/viewer-access.ts'
import type { PageAudience } from '../lib/pages.ts'

/**
 * How long a fetched answer is served without asking again.
 *
 * Group membership changes when IT edits a group, not while a reader works.
 */
export const VIEWER_ACCESS_STALE_TIME_MS = 30 * 60 * 1000

/**
 * Read the signed-in employee's audiences.
 *
 * @param viewer - Who is signed in. Part of the cache key.
 * @returns The viewer's access. No access while loading or after a failure.
 */
export function useViewerAccess(viewer: string): PageAudience[] {
  const query = useQuery({
    queryKey: ['viewer-access', viewer],
    queryFn: fetchViewerAccess,
    staleTime: VIEWER_ACCESS_STALE_TIME_MS,
    retry: false
  })

  return query.data ?? []
}
